import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/** Simple centered modal. Children render inside a card. */
export function Modal({ open, onClose, title, subtitle, children, maxWidth = "max-w-lg", testid }) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="fixed inset-0 z-50 grid place-items-center px-4 overflow-y-auto py-10"
                        data-testid={testid}
                    >
                        <div className={`w-full ${maxWidth} rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl`}>
                            <div className="flex items-start justify-between p-5 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-lg font-semibold">{title}</h3>
                                    {subtitle && (
                                        <p className="mt-1 text-xs text-text-secondary">{subtitle}</p>
                                    )}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-text-secondary hover:text-text-main"
                                    aria-label="Close"
                                    data-testid="modal-close"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="p-5">{children}</div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export function StatusBadge({ status }) {
    const map = {
        draft: "text-text-secondary border-[var(--color-border)] bg-[var(--color-panel-ai)]",
        active: "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/10",
        paused: "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10",
        completed: "text-text-secondary border-[var(--color-border)] bg-[var(--color-panel-ai)]",
        stopped: "text-[var(--color-alert)] border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10",
        pending: "text-text-secondary border-[var(--color-border)] bg-[var(--color-panel-ai)]",
        scheduled: "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/5",
        sending: "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/5",
        sent: "text-text-main border-[var(--color-border)] bg-[var(--color-panel-ai)]",
        opened: "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10",
        clicked: "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/10",
        replied: "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/15",
        bounced: "text-[var(--color-alert)] border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10",
        unsubscribed: "text-[var(--color-alert)] border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10",
        failed: "text-[var(--color-alert)] border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10",
    };
    const cls = map[status] || map.pending;
    return (
        <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}>
            {status}
        </span>
    );
}

export function SandboxBanner({ show }) {
    if (!show) return null;
    return (
        <div
            data-testid="sandbox-banner"
            role="status"
            aria-live="polite"
            className="mb-5 rounded-xl border border-[var(--color-secondary)]/50 bg-[var(--color-secondary)]/10 px-4 py-3 text-sm text-text-main flex items-start gap-3 shadow-[0_0_0_1px_rgba(204, 204, 204,0.08)]"
        >
            <span className="mt-0.5 text-base leading-none select-none" aria-hidden="true">🧪</span>
            <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <strong className="text-[var(--color-secondary)] tracking-wide">
                        SANDBOX MODE
                    </strong>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[var(--color-secondary)]">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-secondary)] animate-pulse-soft" />
                        simulazione attiva
                    </span>
                </div>
                <p className="mt-1 text-xs text-text-secondary leading-relaxed">
                    Le email <strong className="text-text-main">non vengono inviate realmente</strong>.
                    Gli eventi <strong className="text-text-main">opened / clicked / bounced</strong>{" "}
                    sono <strong className="text-text-main">simulati per test</strong>, quindi tutte le
                    statistiche mostrate sotto (open rate, click rate, ecc.) sono fittizie. Imposta{" "}
                    <code className="text-[10px] px-1 py-0.5 rounded bg-[var(--color-panel-ai)] border border-[var(--color-border)]">RESEND_API_KEY</code>{" "}
                    per passare in modalità live.
                </p>
            </div>
        </div>
    );
}
